import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, shareReplay, tap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { ApiResponse } from '../../../shared/models/api-response.dto';
import {
  RequestTypeDto,
  RequestStatusDto,
  RequestPriorityDto,
  RequestTypesResponseDto,
  RequestPrioritiesResponseDto,
  RequestStatusesResponseDto,
  RequestTicketRequestDto,
  RequestTicketResponseDto,
  RequestTicketsListResponseDto,
  RequestTicketsPreviewGroupedResponseDto,
  RequestTicketsPreviewPageResponseDto,
  RequestTicketsPreviewByProjectsGroupedResponseDto,
  RequestTicketsPreviewByProjectsPageResponseDto,
  DeleteRequestTicketResponseDto,
  RequestStatusHistoryResponseDto,
  RequestFunnelResponseDto,
  RequestTicketCommentsResponseDto,
  CreateCommentRequestDto,
  CreateCommentResponseDto,
  CommentReadStateResponseDto,
  CommentReactionsBodyDto,
  CommentReactionsResponseDto,
  PresignedUrlRequestDto,
  PresignedUrlResponseDto,
  RegisterAttachmentRequestDto,
  DeleteAttachmentRequestDto,
  SitePageDto,
  SiteActivitiesResponseDto,
  SiteActivityResponseDto
} from '../models/request.dto';

@Injectable({
  providedIn: 'root'
})
export class RequestService {
  private readonly apiUrl = `${environment.apiHost}/api`;

  // Per-project response caches: repeat visits to the same project within a session reuse
  // the last response instead of refetching; the CRUD methods below invalidate on change.
  private readonly requestTypesCache = new Map<string, Observable<RequestTypesResponseDto>>();
  private readonly requestPrioritiesCache = new Map<string, Observable<RequestPrioritiesResponseDto>>();
  private readonly requestStatusesCache = new Map<string, Observable<RequestStatusesResponseDto>>();

  constructor(private readonly http: HttpClient) {}

  // The HTTP interceptor will automatically add the authorization token
  public getRequestTypes(projectId: string): Observable<RequestTypesResponseDto> {
    let cached = this.requestTypesCache.get(projectId);
    if (!cached) {
      cached = this.http.get<RequestTypesResponseDto>(
        `${this.apiUrl}/projects/${projectId}/request-types/`
      ).pipe(
        catchError((err) => {
          this.requestTypesCache.delete(projectId);
          return throwError(() => err);
        }),
        shareReplay(1)
      );
      this.requestTypesCache.set(projectId, cached);
    }
    return cached;
  }

  public getRequestPriorities(projectId: string): Observable<RequestPrioritiesResponseDto> {
    let cached = this.requestPrioritiesCache.get(projectId);
    if (!cached) {
      cached = this.http.get<RequestPrioritiesResponseDto>(
        `${this.apiUrl}/projects/${projectId}/request-priorities/`
      ).pipe(
        catchError((err) => {
          this.requestPrioritiesCache.delete(projectId);
          return throwError(() => err);
        }),
        shareReplay(1)
      );
      this.requestPrioritiesCache.set(projectId, cached);
    }
    return cached;
  }

  public getRequestStatuses(projectId: string): Observable<RequestStatusesResponseDto> {
    let cached = this.requestStatusesCache.get(projectId);
    if (!cached) {
      cached = this.http.get<RequestStatusesResponseDto>(
        `${this.apiUrl}/projects/${projectId}/request-statuses/`
      ).pipe(
        catchError((err) => {
          this.requestStatusesCache.delete(projectId);
          return throwError(() => err);
        }),
        shareReplay(1)
      );
      this.requestStatusesCache.set(projectId, cached);
    }
    return cached;
  }

  /** Pages belonging to a site (content.Page), e.g. for the New Request "Page" dropdown. */
  public getSitePages(siteId: string): Observable<ApiResponse<SitePageDto[]>> {
    return this.http.get<ApiResponse<SitePageDto[]>>(
      `${this.apiUrl}/sites/${siteId}/pages/`
    );
  }

  /** Activity feed for a site (publish notes + mock events), optionally filtered by category. */
  public getSiteActivities(siteId: string, category?: string): Observable<SiteActivitiesResponseDto> {
    let url = `${this.apiUrl}/sites/${siteId}/activities/`;
    if (category && category !== 'all') url += `?category=${encodeURIComponent(category)}`;
    return this.http.get<SiteActivitiesResponseDto>(url);
  }

  /** Record a publish note (rich-text notes + tagged completed requests) and notify/email members. */
  public publishSiteNotes(siteId: string, body: { notes: any; request_ids: string[]; category?: string | null }): Observable<SiteActivityResponseDto> {
    return this.http.post<SiteActivityResponseDto>(
      `${this.apiUrl}/sites/${siteId}/activities/publish/`,
      body
    );
  }

  // ─── Request Types CRUD ─────────────────────────────────────

  public createRequestType(projectId: string, body: { name: string; is_active?: boolean }): Observable<ApiResponse<RequestTypeDto>> {
    return this.http.post<ApiResponse<RequestTypeDto>>(
      `${this.apiUrl}/request-types/`,
      { ...body, project: projectId }
    ).pipe(tap(() => this.requestTypesCache.delete(projectId)));
  }

  public updateRequestType(id: string, body: { name?: string; project?: string; is_active?: boolean }): Observable<ApiResponse<RequestTypeDto>> {
    return this.http.patch<ApiResponse<RequestTypeDto>>(
      `${this.apiUrl}/request-types/${id}/`,
      body
    ).pipe(tap(() => this.requestTypesCache.clear()));
  }

  public deleteRequestType(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/request-types/${id}/`).pipe(
      tap(() => this.requestTypesCache.clear())
    );
  }

  // ─── Request Statuses CRUD ──────────────────────────────────

  public createRequestStatus(projectId: string, body: { name: string; order?: number; is_active?: boolean }): Observable<ApiResponse<RequestStatusDto>> {
    return this.http.post<ApiResponse<RequestStatusDto>>(
      `${this.apiUrl}/request-statuses/`,
      { ...body, project: projectId }
    ).pipe(tap(() => this.requestStatusesCache.delete(projectId)));
  }

  public updateRequestStatus(id: string, body: { name?: string; order?: number; project?: string; is_active?: boolean }): Observable<ApiResponse<RequestStatusDto>> {
    return this.http.patch<ApiResponse<RequestStatusDto>>(
      `${this.apiUrl}/request-statuses/${id}/`,
      body
    ).pipe(tap(() => this.requestStatusesCache.clear()));
  }

  public deleteRequestStatus(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/request-statuses/${id}/`).pipe(
      tap(() => this.requestStatusesCache.clear())
    );
  }

  // ─── Request Priorities CRUD ──────────────────────────────────

  public createRequestPriority(projectId: string, body: { name: string; order?: number; is_active?: boolean }): Observable<ApiResponse<RequestPriorityDto>> {
    return this.http.post<ApiResponse<RequestPriorityDto>>(
      `${this.apiUrl}/request-priorities/`,
      { ...body, project: projectId }
    ).pipe(tap(() => this.requestPrioritiesCache.delete(projectId)));
  }

  public updateRequestPriority(id: string, body: { name?: string; order?: number; project?: string; is_active?: boolean }): Observable<ApiResponse<RequestPriorityDto>> {
    return this.http.patch<ApiResponse<RequestPriorityDto>>(
      `${this.apiUrl}/request-priorities/${id}/`,
      body
    ).pipe(tap(() => this.requestPrioritiesCache.clear()));
  }

  public deleteRequestPriority(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/request-priorities/${id}/`).pipe(
      tap(() => this.requestPrioritiesCache.clear())
    );
  }

  public createRequestTicket(request: RequestTicketRequestDto): Observable<RequestTicketResponseDto> {
    return this.http.post<RequestTicketResponseDto>(
      `${this.apiUrl}/request-tickets/`,
      request
    );
  }

  /**
   * Get a presigned URL for uploading an attachment to R2.
   * POST /api/attachments/presigned-url/
   */
  public getPresignedUploadUrl(body: PresignedUrlRequestDto): Observable<PresignedUrlResponseDto> {
    return this.http.post<PresignedUrlResponseDto>(
      `${this.apiUrl}/attachments/presigned-url/`,
      body
    );
  }

  /**
   * Upload a file directly to R2 using the presigned URL.
   * No auth header is sent (presigned URL carries the signature).
   */
  public uploadFileToPresignedUrl(uploadUrl: string, file: File): Observable<void> {
    return this.http.put<void>(uploadUrl, file, {
      headers: { 'Content-Type': file.type || 'application/octet-stream' }
    });
  }

  /**
   * Register an attachment on a ticket after it has been uploaded to R2.
   * POST /api/attachments/register/
   * Returns the updated ticket (same shape as GET request-tickets/{id}/).
   */
  public registerAttachment(body: RegisterAttachmentRequestDto): Observable<RequestTicketResponseDto> {
    return this.http.post<RequestTicketResponseDto>(
      `${this.apiUrl}/attachments/register/`,
      body
    );
  }

  /**
   * Remove an attachment from the ticket and delete the object from R2.
   * POST /api/attachments/delete/
   */
  public deleteAttachment(body: DeleteAttachmentRequestDto): Observable<RequestTicketResponseDto> {
    return this.http.post<RequestTicketResponseDto>(
      `${this.apiUrl}/attachments/delete/`,
      body
    );
  }

  public getRequestTickets(projectId: string): Observable<RequestTicketsListResponseDto> {
    return this.http.get<RequestTicketsListResponseDto>(
      `${this.apiUrl}/projects/${projectId}/request-tickets/`
    );
  }

  /**
   * Get page 1 of every active status column for the kanban board (card data only).
   * Response `data` is keyed by status_id. Use getRequestTicketById when the user opens
   * a card for full detail (notes, attachments, request_details).
   */
  public getRequestTicketsPreview(projectId: string): Observable<RequestTicketsPreviewGroupedResponseDto> {
    return this.http.get<RequestTicketsPreviewGroupedResponseDto>(
      `${this.apiUrl}/projects/${projectId}/request-tickets/preview/`,
      { params: { page_size: '8' } }
    );
  }

  /**
   * Get the next page of tickets for a single status column (kanban "load more" on scroll).
   */
  public getRequestTicketsPreviewPage(
    projectId: string,
    statusId: string,
    page: number,
    pageSize = 15
  ): Observable<RequestTicketsPreviewPageResponseDto> {
    return this.http.get<RequestTicketsPreviewPageResponseDto>(
      `${this.apiUrl}/projects/${projectId}/request-tickets/preview/`,
      { params: { status_id: statusId, page: String(page), page_size: String(pageSize) } }
    );
  }

  /**
   * Merged board across multiple projects — GET /api/request-tickets/preview-by-projects/.
   * No status_name: page 1 of every column, keyed by status NAME (case-insensitive merge,
   * already sorted server-side for display order).
   */
  public getRequestTicketsPreviewByProjects(
    projectIds: string[],
    pageSize = 8
  ): Observable<RequestTicketsPreviewByProjectsGroupedResponseDto> {
    return this.http.get<RequestTicketsPreviewByProjectsGroupedResponseDto>(
      `${this.apiUrl}/request-tickets/preview-by-projects/`,
      { params: { project_ids: projectIds.join(','), page_size: String(pageSize) } }
    );
  }

  /**
   * Next page of one merged column (by status name, case-insensitive) across the given projects.
   */
  public getRequestTicketsPreviewByProjectsPage(
    projectIds: string[],
    statusName: string,
    page: number,
    pageSize = 15
  ): Observable<RequestTicketsPreviewByProjectsPageResponseDto> {
    return this.http.get<RequestTicketsPreviewByProjectsPageResponseDto>(
      `${this.apiUrl}/request-tickets/preview-by-projects/`,
      {
        params: {
          project_ids: projectIds.join(','),
          status_name: statusName,
          page: String(page),
          page_size: String(pageSize)
        }
      }
    );
  }

  /**
   * Get full request ticket detail (notes, attachments, request_details). Call when user opens a card.
   */
  public getRequestTicketById(ticketId: string): Observable<RequestTicketResponseDto> {
    return this.http.get<RequestTicketResponseDto>(
      `${this.apiUrl}/request-tickets/${ticketId}/`
    );
  }

  /**
   * Update a request ticket (PATCH). Body can include e.g. task_name, type, priority, status,
   * notes, deadline, assignees (array of UUIDs), sites, page (UUID or null to clear).
   * Response shape mostly matches GET, except `page`/`page_name` come back flat here
   * (not nested like GET's `page: { page_id, page_name }`) — reload via getRequestTicketById
   * if you need the nested shape.
   */
  public updateRequestTicket(requestId: string, request: Partial<RequestTicketRequestDto>): Observable<RequestTicketResponseDto> {
    return this.http.patch<RequestTicketResponseDto>(
      `${this.apiUrl}/request-tickets/${requestId}/`,
      request
    );
  }

  public deleteRequestTicket(requestId: string): Observable<DeleteRequestTicketResponseDto> {
    return this.http.delete<DeleteRequestTicketResponseDto>(
      `${this.apiUrl}/request-tickets/${requestId}/`
    );
  }

  /** Restore a soft-deleted request ticket. Returns the full ticket detail (now is_deleted=false). */
  public restoreRequestTicket(requestId: string): Observable<RequestTicketResponseDto> {
    return this.http.post<RequestTicketResponseDto>(
      `${this.apiUrl}/request-tickets/${requestId}/restore/`,
      {}
    );
  }

  public getRequestStatusHistory(ticketId: string): Observable<RequestStatusHistoryResponseDto> {
    return this.http.get<RequestStatusHistoryResponseDto>(
      `${this.apiUrl}/request-tickets/${ticketId}/status-history`
    );
  }

  public getRequestTicketComments(ticketId: string): Observable<RequestTicketCommentsResponseDto> {
    return this.http.get<RequestTicketCommentsResponseDto>(
      `${this.apiUrl}/request-tickets/${ticketId}/comments/`
    );
  }

  public getCommentReplies(commentId: string): Observable<RequestTicketCommentsResponseDto> {
    return this.http.get<RequestTicketCommentsResponseDto>(
      `${this.apiUrl}/comments/${commentId}/replies`
    );
  }

  /** When the caller last viewed this ticket's comments (null = never) — drives unread styling. */
  public getCommentsReadState(ticketId: string): Observable<CommentReadStateResponseDto> {
    return this.http.get<CommentReadStateResponseDto>(
      `${this.apiUrl}/request-tickets/${ticketId}/comments/read-state/`
    );
  }

  /** Mark the caller as having viewed this ticket's comments now. */
  public markCommentsViewed(ticketId: string): Observable<CommentReadStateResponseDto> {
    return this.http.post<CommentReadStateResponseDto>(
      `${this.apiUrl}/request-tickets/${ticketId}/comments/mark-viewed/`,
      {}
    );
  }

  /**
   * Create a new comment (top-level or reply) on a ticket.
   * @param ticketId - Ticket UUID
   * @param body - content, comment_type, is_internal, metadata; optional parent for replies
   */
  public createComment(ticketId: string, body: CreateCommentRequestDto): Observable<CreateCommentResponseDto> {
    return this.http.post<CreateCommentResponseDto>(
      `${this.apiUrl}/request-tickets/${ticketId}/comments/`,
      body
    );
  }

  /**
   * Delete a comment (top-level or reply) from a ticket.
   * @param commentId - Comment UUID
   */
  public deleteComment(commentId: string): Observable<{ status: string; message?: string }> {
    return this.http.delete<{ status: string; message?: string }>(
      `${this.apiUrl}/comments/${commentId}/`
    );
  }

  /**
   * Update a comment (content or mentions). For reactions use updateCommentReactions.
   */
  public updateComment(commentId: string, body: { content?: string; metadata?: { mentions?: string[] } }): Observable<CreateCommentResponseDto> {
    return this.http.patch<CreateCommentResponseDto>(
      `${this.apiUrl}/comments/${commentId}/`,
      body
    );
  }

  /**
   * Update comment reactions (PATCH /api/comments/{id}/reactions/).
   * Body: { reactions: { thumbs_up: 3, thumbs_down: 0, checkmark: 1, laugh: null } }
   */
  public updateCommentReactions(commentId: string, body: CommentReactionsBodyDto): Observable<CommentReactionsResponseDto> {
    return this.http.patch<CommentReactionsResponseDto>(
      `${this.apiUrl}/comments/${commentId}/reactions/`,
      body
    );
  }

  public getRequestFunnel(projectId: string): Observable<RequestFunnelResponseDto> {
    return this.http.get<RequestFunnelResponseDto>(
      `${this.apiUrl}/projects/${projectId}/request-funnel/`
    );
  }
}
